import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, HStack, IconButton, Tooltip } from "@chakra-ui/react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import {
  createChart,
  createSeriesMarkers,
  IChartApi,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  CandlestickSeries,
  LineSeries,
  BaselineSeries,
  HistogramSeries,
  LineData,
  LineType,
  Time,
  UTCTimestamp,
  SeriesMarker,
} from "lightweight-charts";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { REPORTS_SYMBOL_BADGE_CLASS } from "../../../Reports/components/ReportsCardChrome";
import { ExecutionCandle, ExecutionCandlesRegimeOverlay, ExecutionTradeOverlay } from "../../../../../types/trade";
import { humanizeManagementAction } from "../../../../../utils/managementActionLabels";
import {
  buildRegimeHistogramData,
  prepareExecutionCandlesWithRegime,
} from "./executionCandlesRegimeOverlay";
import {
  resolveOverlaySlTpLevels,
  resolveOverlayExitSegment,
  resolveActualExitOverlaySegment,
  isExecutionTradeLosing,
  SL_COLOR,
  TP_COLOR,
} from "../../../../../utils/executionTradeOverlayLevels";
import { useTradeReviewThemeColors } from "./tradeReviewTheme";
import {
  resolveEntryPlotOnCandles,
  snapToContainingCandleTime,
} from "./executionCandlesEntryPlot";
import {
  listChangedSimulatedTrades,
  simulatedSlTpLevels,
} from "../../../../../utils/simulationTradeOverlay";
import { mapCumulativePnlToBaselineBand, resolveOverlayAdjustedTradePnl, resolveTradePnlRealizationTime } from "./executionCandlesPnlOverlay";
import {
  COHORT_PREVIEW_OTHER_RGBA,
  cohortPreviewDirectionColor,
  cohortPreviewMarkerPosition,
  cohortPreviewMarkerShape,
  cohortPreviewOutcomeLabel,
  cohortPreviewOutcomeTextColor,
} from "../../../../../utils/cohortTradesResolve";

type VisibleRange = { from: UTCTimestamp; to: UTCTimestamp } | null;
type VisibleLogicalRange = { from: number; to: number } | null;

type CohortBucketCounts = {
  winLong: number;
  winShort: number;
  lossLong: number;
  lossShort: number;
  other: number;
};

function emptyCohortBucketCounts(): CohortBucketCounts {
  return { winLong: 0, winShort: 0, lossLong: 0, lossShort: 0, other: 0 };
}

function resolveTradeCohortOutcome(trade: ExecutionTradeOverlay): "win" | "loss" {
  if (trade.cohort_outcome === "loss") return "loss";
  if (trade.cohort_outcome === "win") return "win";
  return isExecutionTradeLosing(trade) ? "loss" : "win";
}

function pushEntryPointData(
  point: LineData,
  direction: string | null | undefined,
  shouldDim: boolean,
  targets: {
    longEntryData: LineData[];
    shortEntryData: LineData[];
    longEntryDimData: LineData[];
    shortEntryDimData: LineData[];
  },
): void {
  if (direction === "short") {
    if (shouldDim) targets.shortEntryDimData.push(point);
    else targets.shortEntryData.push(point);
    return;
  }
  if (shouldDim) targets.longEntryDimData.push(point);
  else targets.longEntryData.push(point);
}

function accumulateCohortBucketTrade(bucket: CohortBucketCounts, trade: ExecutionTradeOverlay): void {
  if (trade.bucket_overlay === "out_of_bucket") {
    bucket.other += 1;
    return;
  }
  // Treat missing bucket_overlay as in-bucket (Trade Log default / full cohort).
  if (trade.bucket_overlay != null && trade.bucket_overlay !== "in_bucket") return;
  const isShort = trade.direction === "short";
  const isLoss = resolveTradeCohortOutcome(trade) === "loss";
  if (isLoss) {
    if (isShort) bucket.lossShort += 1;
    else bucket.lossLong += 1;
  } else {
    if (isShort) bucket.winShort += 1;
    else bucket.winLong += 1;
  }
}

function pushCohortBucketMarkers(
  markers: SeriesMarker<Time>[],
  timeNum: number,
  bucket: CohortBucketCounts,
  dimmed = false,
): void {
  pushOutcomeDirectionMarkers(markers, timeNum, bucket, 0, dimmed);
  pushCountMarker(
    markers,
    timeNum,
    bucket.other,
    dimmed ? "rgba(148, 163, 184, 0.5)" : COHORT_PREVIEW_OTHER_RGBA,
    "circle",
  );
}

type PolicyBucketCounts = {
  keptLongs: number;
  keptShorts: number;
  removedWinLong: number;
  removedWinShort: number;
  removedLossLong: number;
  removedLossShort: number;
};

function emptyPolicyBucketCounts(): PolicyBucketCounts {
  return {
    keptLongs: 0,
    keptShorts: 0,
    removedWinLong: 0,
    removedWinShort: 0,
    removedLossLong: 0,
    removedLossShort: 0,
  };
}

function accumulatePolicyBucketTrade(bucket: PolicyBucketCounts, trade: ExecutionTradeOverlay): void {
  const isShort = trade.direction === "short";
  if (trade.policy_overlay === "kept") {
    if (isShort) bucket.keptShorts += 1;
    else bucket.keptLongs += 1;
    return;
  }
  if (trade.policy_overlay !== "removed") return;
  const isLoss = resolveTradeCohortOutcome(trade) === "loss";
  if (isLoss) {
    if (isShort) bucket.removedLossShort += 1;
    else bucket.removedLossLong += 1;
  } else {
    if (isShort) bucket.removedWinShort += 1;
    else bucket.removedWinLong += 1;
  }
}

function pushCountMarker(
  markers: SeriesMarker<Time>[],
  timeNum: number,
  count: number,
  color: string,
  shape: "arrowUp" | "arrowDown" | "circle",
  sizeBoost = 0,
  opts?: {
    position?: "aboveBar" | "belowBar" | "inBar";
    text?: string;
  },
): void {
  if (count <= 0) return;
  const size = Math.min(10, 2 + Math.round(Math.sqrt(count)) + sizeBoost);
  markers.push({
    time: timeNum as unknown as Time,
    position: opts?.position ?? "inBar",
    color,
    shape: shape as any,
    size,
    text: opts?.text,
    zOrder: "top",
  } as any);
}

function pushLabeledOutcomeMarker(
  markers: SeriesMarker<Time>[],
  timeNum: number,
  direction: "long" | "short",
  outcome: "win" | "loss",
  count: number,
  sizeBoost = 0,
  dimmed = false,
): void {
  if (count <= 0) return;
  const position = cohortPreviewMarkerPosition(direction);
  const shape = cohortPreviewMarkerShape(direction);
  const time = timeNum as unknown as Time;
  if (dimmed) {
    markers.push({
      time,
      position,
      color:
        direction === "short" ? "rgba(224, 1, 1, 0.5)" : "rgba(6, 227, 99, 0.5)",
      shape: shape as any,
      size: Math.min(3, 1 + sizeBoost),
      zOrder: "top",
    } as any);
    markers.push({
      time,
      position,
      color:
        outcome === "loss" ? "rgba(224, 1, 1, 0.5)" : "rgba(6, 227, 99, 0.5)",
      shape: shape as any,
      size: 0,
      text: cohortPreviewOutcomeLabel(direction, outcome, count),
      zOrder: "top",
    } as any);
    return;
  }
  const arrowSize = Math.min(4, 2 + sizeBoost + (count > 1 ? 1 : 0));
  // LWC uses one `color` for shape + text. Split so arrows stay direction-colored
  // while labels use win/loss colors (size 0 skips drawing the shape).
  markers.push({
    time,
    position,
    color: cohortPreviewDirectionColor(direction),
    shape: shape as any,
    size: arrowSize,
    zOrder: "top",
  } as any);
  markers.push({
    time,
    position,
    color: cohortPreviewOutcomeTextColor(outcome),
    shape: shape as any,
    size: 0,
    text: cohortPreviewOutcomeLabel(direction, outcome, count),
    zOrder: "top",
  } as any);
}

function pushOutcomeDirectionMarkers(
  markers: SeriesMarker<Time>[],
  timeNum: number,
  counts: {
    winLong: number;
    winShort: number;
    lossLong: number;
    lossShort: number;
  },
  sizeBoost = 0,
  dimmed = false,
): void {
  pushLabeledOutcomeMarker(markers, timeNum, "long", "win", counts.winLong, sizeBoost, dimmed);
  pushLabeledOutcomeMarker(markers, timeNum, "long", "loss", counts.lossLong, sizeBoost, dimmed);
  pushLabeledOutcomeMarker(markers, timeNum, "short", "win", counts.winShort, sizeBoost, dimmed);
  pushLabeledOutcomeMarker(markers, timeNum, "short", "loss", counts.lossShort, sizeBoost, dimmed);
}

function pushPolicyBucketMarkers(
  markers: SeriesMarker<Time>[],
  timeNum: number,
  bucket: PolicyBucketCounts,
  opts?: {
    /** When a trade is selected, fade non-selected overview markers to ~50%. */
    dimUnselected?: boolean;
  },
): void {
  const dimUnselected = opts?.dimUnselected === true;

  if (bucket.keptLongs > 0) {
    pushCountMarker(
      markers,
      timeNum,
      bucket.keptLongs,
      dimUnselected ? "rgba(6, 227, 99, 0.5)" : "rgba(6, 227, 99, 0.58)",
      "circle",
    );
  }
  if (bucket.keptShorts > 0) {
    pushCountMarker(
      markers,
      timeNum,
      bucket.keptShorts,
      dimUnselected ? "rgba(224, 1, 1, 0.5)" : "rgba(224, 1, 1, 0.58)",
      "circle",
    );
  }
  pushOutcomeDirectionMarkers(
    markers,
    timeNum,
    {
      winLong: bucket.removedWinLong,
      winShort: bucket.removedWinShort,
      lossLong: bucket.removedLossLong,
      lossShort: bucket.removedLossShort,
    },
    0,
    dimUnselected,
  );
}

type CandlePoint = {
  time: UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
  color?: string;
  borderColor?: string;
  wickColor?: string;
};

const FOCUS_DIMMED_CANDLE_BODY = "rgba(142, 151, 166, 0.28)";
const FOCUS_DIMMED_CANDLE_WICK = "rgba(142, 151, 166, 0.22)";

function toUtcTimestampSeconds(isoOrDateString: string): UTCTimestamp | null {
  // Backend sometimes returns timestamps with microseconds, e.g.
  // "2026-03-16T10:15:18.596000+00:00", which native Date parsing
  // treats as invalid in some environments. Normalize to at most
  // millisecond precision before constructing the Date.
  let raw = isoOrDateString;

  if (typeof raw === "string") {
    // Split off timezone part (+HH:MM or -HH:MM) if present
    const tzMatch = raw.match(/([+-]\d\d:?\d\d)$/);
    const tz = tzMatch ? tzMatch[1] : "";
    const base = tz ? raw.slice(0, -tz.length) : raw;

    const fracMatch = base.match(/^(.*\d\:\d\d\:\d\d)\.(\d+)$|^(.*\d\:\d\d\:\d\d)$/);
    if (fracMatch) {
      const main = (fracMatch[1] || fracMatch[3]) ?? base;
      const frac = fracMatch[2];
      if (frac) {
        const msFrac = frac.slice(0, 3).padEnd(3, "0");
        raw = `${main}.${msFrac}${tz}`;
      } else {
        raw = `${main}${tz}`;
      }
    }
  }

  const d = new Date(raw);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 1000) as UTCTimestamp;
}

function snapToNearestCandleTime(
  candles: CandlePoint[],
  target: UTCTimestamp | null
): UTCTimestamp | null {
  if (!target || !candles.length) return target;
  // Find the candle whose time is closest to target (within the same day window).
  let best: UTCTimestamp | null = null;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const c of candles) {
    const diff = Math.abs(c.time - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = c.time;
    }
  }
  return best ?? target;
}

function clampToCandleRange(candle: CandlePoint | undefined, price: number): number {
  if (!candle) return price;
  const lo = Math.min(candle.low, candle.high);
  const hi = Math.max(candle.low, candle.high);
  if (price < lo) return lo;
  if (price > hi) return hi;
  return price;
}

const SIMULATED_EXIT_MARKER_COLOR = "#38BBD1";
const MANAGEMENT_EVENT_MARKER_COLOR = "#A855F7";

function parseManagementEventFields(evt: Record<string, unknown>): {
  timeRaw: unknown;
  price: number | null;
  label: string;
} {
  const timeRaw = evt.event_time ?? evt.time ?? evt.timestamp ?? evt.at ?? null;
  const priceRaw = evt.price ?? evt.event_price ?? evt.level ?? evt.stop_price ?? null;
  const price =
    priceRaw != null && Number.isFinite(Number(priceRaw)) ? Number(priceRaw) : null;
  const type = String(evt.type ?? evt.event_type ?? "event");
  const params =
    evt.params && typeof evt.params === "object"
      ? (evt.params as Record<string, unknown>)
      : evt;
  let label = type.replace(/_/g, " ");
  try {
    label = humanizeManagementAction({ type, params });
  } catch {
    // keep fallback label
  }
  return { timeRaw, price, label };
}

export interface ExecutionCandlesChartProps {
  candles: ExecutionCandle[];
  trades: ExecutionTradeOverlay[];
  baselineTrades?: ExecutionTradeOverlay[];
  simulatedTrades?: Array<ExecutionTradeOverlay & { delta?: Record<string, unknown> }>;
  managementEvents?: Array<Record<string, unknown>>;
  /** TM overlay: lines only (no sim-exit / management-event dot markers). */
  simulationLineOverlay?: boolean;
  selectedTradeId: string | null;
  showPnlBackgroundOverlay?: boolean;
  /** Opt-in market-condition bgcolor (paired with `regimeOverlay`). */
  showRegimeOverlay?: boolean;
  regimeOverlay?: ExecutionCandlesRegimeOverlay | null;
  focusTradeId?: string | null;
  onFocusTradeHandled?: () => void;
  onSelectTrade?: (tradeId: string | null) => void;
  height?: number;
  /** Shown on-chart (e.g. BTCUSDC). */
  instrumentLabel?: string;
  /** Exit Placement: legs replayed — omit sim SL/TP lines for legs not run. */
  simulatedLegs?: Array<"sl" | "tp">;
  /** Pan/zoom/fit toolbar (default on for Trade Review). */
  showNavigationControls?: boolean;
  visualStyle?: "default" | "reports";
}

const PAN_SHIFT_FRACTION = 0.35;
const ZOOM_IN_FACTOR = 0.72;
const ZOOM_OUT_FACTOR = 1.38;
const MIN_VISIBLE_BARS = 12;
const LOGICAL_EDGE_EPS = 0.25;
/** Minimum px between bars — lower = more bars visible when zoomed out (1h overview). */
const MIN_BAR_SPACING_PX = 1;
/** Defer React viewport state so toolbar pan/zoom does not rerun the full overlay effect every click. */
const VIEWPORT_STATE_DEBOUNCE_MS = 120;

type ChartNavigationBounds = {
  /** Widest zoom-out span (from fitContent or toolbar zoom out). */
  maxSpan: number;
  dataMin: number;
  dataMax: number;
  /** fitContent trailing margin past the last bar — cap pan right only. */
  extraRight: number;
  /** fitContent leading margin before the first bar. */
  extraLeft: number;
};

function captureNavigationBounds(
  range: { from: number; to: number },
  candleCount: number
): ChartNavigationBounds {
  const dataMin = 0;
  const dataMax = Math.max(0, candleCount - 1);
  const visibleSpan = Math.max(1, range.to - range.from);
  const maxSpan = Math.max(visibleSpan, fullChartZoomSpan(candleCount));
  return {
    maxSpan,
    dataMin,
    dataMax,
    extraRight: Math.max(0, range.to - dataMax),
    extraLeft: Math.min(0, range.from - dataMin),
  };
}

function fullChartZoomSpan(candleCount: number): number {
  return Math.max(MIN_VISIBLE_BARS, Math.max(0, candleCount - 1));
}

/** Center sparse OHLC in the viewport; fit full series when enough bars exist. */
function fitChartViewportToCandles(
  chart: IChartApi,
  candleCount: number
): { from: number; to: number } | null {
  if (candleCount <= 0) return null;
  const timeScale = chart.timeScale();

  if (candleCount >= MIN_VISIBLE_BARS) {
    timeScale.fitContent();
    const logical = timeScale.getVisibleLogicalRange();
    if (logical && logical.from != null && logical.to != null) {
      return { from: Number(logical.from), to: Number(logical.to) };
    }
    const sidePadding = Math.min(8, Math.max(2, Math.ceil(candleCount * 0.1)));
    return {
      from: -sidePadding,
      to: candleCount - 1 + sidePadding,
    };
  }

  const mid = (candleCount - 1) / 2;
  const halfSpan = MIN_VISIBLE_BARS / 2;
  const from = mid - halfSpan;
  const to = mid + halfSpan;

  timeScale.setVisibleLogicalRange({ from, to });
  return { from, to };
}

function maybeExpandNavigationBounds(
  prev: ChartNavigationBounds | null,
  range: { from: number; to: number },
  candleCount: number
): ChartNavigationBounds {
  const next = captureNavigationBounds(range, candleCount);
  if (!prev || next.maxSpan > prev.maxSpan + 1e-6) return next;
  return prev;
}

/** LW throws when logical edges have no bar times — never call getVisibleRange unguarded. */
function safeGetVisibleRange(
  timeScale: { getVisibleRange: () => unknown }
): { from: UTCTimestamp; to: UTCTimestamp } | null {
  try {
    const r = timeScale.getVisibleRange() as { from?: unknown; to?: unknown } | null;
    if (r && r.from != null && r.to != null) {
      return { from: r.from as UTCTimestamp, to: r.to as UTCTimestamp };
    }
  } catch {
    // ignore
  }
  return null;
}

/** Scrollable pan limits — at full zoom allow sliding within fit slack (not glued to center). */
function panLimitsForSpan(
  span: number,
  bounds: ChartNavigationBounds
): { minFrom: number; maxFrom: number; maxTo: number } {
  const safeSpan = Math.max(1, span);
  const ratio = Math.min(1, Math.max(0, safeSpan / bounds.maxSpan));
  let minFrom = bounds.extraLeft * ratio;
  let maxTo = bounds.dataMax + bounds.extraRight * ratio;
  let maxFrom = maxTo - safeSpan;
  // When span ≈ fit width, ratio≈1 makes minFrom≈maxFrom (center lock). Allow the fit
  // padding as travel so toolbar/mouse pan can shift without snapping back.
  if (maxFrom < minFrom + 1e-6) {
    const pad = Math.max(Math.abs(bounds.extraLeft), bounds.extraRight, 1);
    minFrom = bounds.extraLeft - pad;
    maxTo = bounds.dataMax + bounds.extraRight + pad;
    maxFrom = maxTo - safeSpan;
  }
  return { minFrom, maxFrom, maxTo };
}

/** Crosshair timestamp — keep full detail for precise inspection. */
function formatExecutionChartCrosshairTime(time: Time): string {
  const ts = Number(time);
  if (!Number.isFinite(ts)) return "";
  const d = new Date(ts * 1000);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/**
 * X-axis tick labels should stay compact to avoid overlap on dense windows.
 * Use short day/month + time (no year).
 */
function formatExecutionChartTickTime(time: Time): string {
  const ts = Number(time);
  if (!Number.isFinite(ts)) return "";
  const d = new Date(ts * 1000);
  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
  }).format(d);
  const timePart = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
  return `${datePart} ${timePart}`;
}

function resolvePriceFormat(candles: CandlePoint[]): { precision: number; minMove: number } {
  if (!candles.length) return { precision: 2, minMove: 0.01 };
  let max = 0;
  for (const c of candles) {
    max = Math.max(max, c.high, c.low, c.open, c.close);
  }
  if (max < 0.0001) return { precision: 8, minMove: 0.00000001 };
  if (max < 0.01) return { precision: 6, minMove: 0.000001 };
  if (max < 1) return { precision: 4, minMove: 0.0001 };
  return { precision: 2, minMove: 0.01 };
}

const ExecutionCandlesChart: React.FC<ExecutionCandlesChartProps> = ({
  candles,
  trades,
  baselineTrades,
  simulatedTrades,
  managementEvents,
  simulationLineOverlay = false,
  selectedTradeId,
  showPnlBackgroundOverlay = true,
  showRegimeOverlay = false,
  regimeOverlay = null,
  focusTradeId,
  onFocusTradeHandled,
  onSelectTrade,
  height = 520,
  instrumentLabel,
  simulatedLegs,
  showNavigationControls = true,
  visualStyle = "default",
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const markersPluginRef = useRef<ISeriesMarkersPluginApi<any> | null>(null);
  const longEntrySeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const shortEntrySeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const longEntryDimSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const shortEntryDimSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  /** Policy-removed long/short entries: distinct from kept green/red. */
  const longRemovedEntrySeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const shortRemovedEntrySeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const slSegmentSeriesRefs = useRef<Array<ISeriesApi<"Line">>>([]);
  const tpSegmentSeriesRefs = useRef<Array<ISeriesApi<"Line">>>([]);
  const simSlSegmentSeriesRefs = useRef<Array<ISeriesApi<"Line">>>([]);
  const simTpSegmentSeriesRefs = useRef<Array<ISeriesApi<"Line">>>([]);
  const exitSegmentSeriesRefs = useRef<Array<ISeriesApi<"Line">>>([]);
  const pnlZoneSeriesRefs = useRef<Array<ISeriesApi<"Baseline">>>([]);
  const pnlBackgroundSeriesRef = useRef<ISeriesApi<"Baseline"> | null>(null);
  const regimeBackgroundSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const lastMarkersRef = useRef<SeriesMarker<Time>[]>([]);
  const clickableEntriesRef = useRef<Array<{ tradeId: string; time: number; price: number }>>([]);
  const lastLongEntryDataRef = useRef<LineData[]>([]);
  const lastShortEntryDataRef = useRef<LineData[]>([]);
  const [visibleRange, setVisibleRange] = useState<VisibleRange>(null);
  const [visibleLogicalRange, setVisibleLogicalRange] = useState<VisibleLogicalRange>(null);
  const hasFitOnceRef = useRef(false);
  const lastAutoFitViewportKeyRef = useRef<string>("");
  const skipViewportRestoreOnceRef = useRef(false);
  const navigationBoundsRef = useRef<ChartNavigationBounds | null>(null);
  const [chartVersion, setChartVersion] = useState(0);
  const preparedCandleCountRef = useRef(0);
  const viewportSyncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markerRefreshRafRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const lastSizeRef = useRef<{ w: number; h: number } | null>(null);
  const selectedTradeIdRef = useRef<string | null>(selectedTradeId);
  const onSelectTradeRef = useRef<typeof onSelectTrade>(onSelectTrade);

  const {
    chartBg: bgColor,
    chartGridColor: gridColor,
    chartTextColor: textColor,
    chartNavBtnBg: navBtnBg,
    chartNavBtnBorder: navBtnBorder,
    borderColor,
  } = useTradeReviewThemeColors(visualStyle);

  const preparedRegimeBars = useMemo(
    () =>
      prepareExecutionCandlesWithRegime({
        candles,
        regimeOverlay,
        focusTradeId,
        focusDimmedBody: FOCUS_DIMMED_CANDLE_BODY,
        focusDimmedWick: FOCUS_DIMMED_CANDLE_WICK,
      }),
    [candles, focusTradeId, regimeOverlay]
  );

  const preparedCandles: CandlePoint[] = useMemo(
    () =>
      preparedRegimeBars.map(({ regimeCondition: _regimeCondition, ...point }) => ({
        ...point,
        time: point.time as UTCTimestamp,
      })),
    [preparedRegimeBars]
  );
  preparedCandleCountRef.current = preparedCandles.length;

  const candleViewportKey = useMemo(() => {
    if (!preparedCandles.length) return "";
    const first = preparedCandles[0]?.time ?? "";
    const last = preparedCandles[preparedCandles.length - 1]?.time ?? "";
    return `${preparedCandles.length}:${first}:${last}`;
  }, [preparedCandles]);

  const queueViewportStateSync = useCallback(
    (logical: { from: number; to: number } | null, time: VisibleRange | null) => {
      if (viewportSyncDebounceRef.current) clearTimeout(viewportSyncDebounceRef.current);
      viewportSyncDebounceRef.current = setTimeout(() => {
        if (logical) {
          setVisibleLogicalRange(logical);
          const n = preparedCandleCountRef.current;
          if (n > 0) {
            navigationBoundsRef.current = maybeExpandNavigationBounds(
              navigationBoundsRef.current,
              logical,
              n
            );
          }
        } else {
          setVisibleLogicalRange(null);
        }
        setVisibleRange(time);
        viewportSyncDebounceRef.current = null;
      }, VIEWPORT_STATE_DEBOUNCE_MS);
    },
    []
  );
  const queueViewportStateSyncRef = useRef(queueViewportStateSync);
  queueViewportStateSyncRef.current = queueViewportStateSync;

  useEffect(
    () => () => {
      if (viewportSyncDebounceRef.current) clearTimeout(viewportSyncDebounceRef.current);
      if (markerRefreshRafRef.current != null) cancelAnimationFrame(markerRefreshRafRef.current);
    },
    []
  );

  const visibleTrades = useMemo(() => trades || [], [trades]);
  const visibleSimulatedTrades = useMemo(() => simulatedTrades || [], [simulatedTrades]);
  const visibleManagementEvents = useMemo(() => managementEvents || [], [managementEvents]);
  const hasSimulationLayers = visibleSimulatedTrades.length > 0 || visibleManagementEvents.length > 0;
  const hasPolicyOverlay = useMemo(() => {
    for (const t of visibleTrades || []) {
      const tag = (t as any)?.policy_overlay;
      if (tag === "kept" || tag === "removed") return true;
    }
    return false;
  }, [visibleTrades]);
  /** Removed / removed winners|losers tabs: chart trades are removed-only — include their PnL. */
  const includeRemovedInPnl = useMemo(() => {
    if (!hasPolicyOverlay || visibleTrades.length === 0) return false;
    return !visibleTrades.some((t) => t.policy_overlay === "kept");
  }, [hasPolicyOverlay, visibleTrades]);
  const hasBucketHighlight = useMemo(() => {
    for (const t of visibleTrades || []) {
      if (t.bucket_overlay === "in_bucket" || t.bucket_overlay === "out_of_bucket") return true;
    }
    return false;
  }, [visibleTrades]);
  const candleByTime = useMemo(() => {
    const m = new Map<number, CandlePoint>();
    for (const c of preparedCandles) m.set(Number(c.time), c);
    return m;
  }, [preparedCandles]);
  const candleMinTime = preparedCandles.length ? preparedCandles[0].time : null;
  const candleMaxTime = preparedCandles.length
    ? preparedCandles[preparedCandles.length - 1].time
    : null;

  const secondsPerCandle = useMemo(() => {
    if (preparedCandles.length < 2) return 60;
    const first = preparedCandles[0].time;
    const last = preparedCandles[preparedCandles.length - 1].time;
    const span = Math.max(1, last - first);
    return Math.max(1, Math.round(span / (preparedCandles.length - 1)));
  }, [preparedCandles]);

  const changedSimulatedTrades = useMemo(
    () =>
      listChangedSimulatedTrades(
        baselineTrades ?? visibleTrades,
        simulatedTrades,
        secondsPerCandle
      ),
    [baselineTrades, visibleTrades, simulatedTrades, secondsPerCandle]
  );
  const changedSimulatedTradeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const t of changedSimulatedTrades) {
      if (t.trade_id) ids.add(t.trade_id);
    }
    return ids;
  }, [changedSimulatedTrades]);
  const baselineTradeById = useMemo(() => {
    const m = new Map<string, ExecutionTradeOverlay>();
    for (const t of baselineTrades ?? visibleTrades) {
      if (t.trade_id) m.set(t.trade_id, t);
    }
    return m;
  }, [baselineTrades, visibleTrades]);

  const detailedBarsThreshold = useMemo(() => {
    // Adaptive threshold by candle interval to avoid clutter on higher TFs.
    // Target ~6 hours of visible range before switching to per-trade SL/TP lines:
    // 1m => 150 bars, 5m => 72, 15m => 24, 1h => 6, 4h => 5 (floor), etc.
    const byTimeSpan = Math.round((6 * 60 * 60) / Math.max(1, secondsPerCandle));
    return Math.max(5, Math.min(150, byTimeSpan));
  }, [secondsPerCandle]);

  const isZoomedIn = useMemo(() => {
    if (!visibleLogicalRange) return false;
    const visibleBars = Math.max(1, Math.round(visibleLogicalRange.to - visibleLogicalRange.from));
    return visibleBars <= detailedBarsThreshold;
  }, [visibleLogicalRange, detailedBarsThreshold]);

  /** Bucketed entry markers — hide per-trade SL/TP spaghetti until a trade is picked. */
  const useBucketedEntryMarkers =
    (visibleTrades.length > 1 && !isZoomedIn) || (simulationLineOverlay && !selectedTradeId);

  const navigationDisabled = preparedCandles.length === 0;

  const readVisibleLogicalRangeFromChart = useCallback((): { from: number; to: number } | null => {
    const chart = chartRef.current;
    if (!chart) return null;
    try {
      const r = chart.timeScale().getVisibleLogicalRange();
      if (!r || !Number.isFinite(r.from) || !Number.isFinite(r.to) || r.to <= r.from) return null;
      return { from: r.from, to: r.to };
    } catch {
      return null;
    }
  }, []);

  const setVisibleLogicalRangeOnChart = useCallback((from: number, to: number) => {
    const chart = chartRef.current;
    if (!chart || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;
    try {
      chart.timeScale().setVisibleLogicalRange({ from, to });
    } catch {
      // ignore if chart is not ready
    }
  }, []);

  const panChart = useCallback(
    (direction: -1 | 1) => {
      const current = readVisibleLogicalRangeFromChart();
      const n = preparedCandles.length;
      if (!current || n === 0) return;

      const bounds = navigationBoundsRef.current;
      const dataMin = 0;
      const dataMax = Math.max(0, n - 1);
      const span = current.to - current.from;
      const limits = bounds ? panLimitsForSpan(span, bounds) : null;
      const panMinFrom = limits?.minFrom ?? dataMin;
      const panMaxFrom = limits?.maxFrom ?? dataMax - span;
      const panMaxTo = limits?.maxTo ?? dataMax;

      if (direction > 0 && (current.to >= panMaxTo - LOGICAL_EDGE_EPS || current.from >= panMaxFrom - LOGICAL_EDGE_EPS)) {
        return;
      }
      if (direction < 0 && current.from <= panMinFrom + LOGICAL_EDGE_EPS) return;

      const shift = span * PAN_SHIFT_FRACTION * direction;
      let nextFrom = current.from + shift;
      let nextTo = current.to + shift;

      // Translate only — keep span fixed; stop at scroll extents.
      if (nextFrom < panMinFrom) {
        nextTo += panMinFrom - nextFrom;
        nextFrom = panMinFrom;
      }
      if (nextFrom > panMaxFrom) {
        nextTo -= nextFrom - panMaxFrom;
        nextFrom = panMaxFrom;
      }
      if (nextTo > panMaxTo) {
        nextFrom -= nextTo - panMaxTo;
        nextTo = panMaxTo;
      }
      if (nextFrom < panMinFrom) {
        nextFrom = panMinFrom;
        nextTo = Math.min(panMaxTo, nextFrom + span);
      }

      if (Math.abs(nextFrom - current.from) < 1e-6 && Math.abs(nextTo - current.to) < 1e-6) return;
      setVisibleLogicalRangeOnChart(nextFrom, nextTo);
    },
    [preparedCandles.length, readVisibleLogicalRangeFromChart, setVisibleLogicalRangeOnChart]
  );

  const zoomChart = useCallback(
    (zoomIn: boolean) => {
      const current = readVisibleLogicalRangeFromChart();
      const n = preparedCandles.length;
      if (!current || n === 0) return;

      const dataMin = 0;
      const dataMax = Math.max(0, n - 1);
      const span = current.to - current.from;
      const minSpan = MIN_VISIBLE_BARS;
      const maxSpanLimit = fullChartZoomSpan(n);

      if (zoomIn && span <= minSpan + 1e-6) return;
      if (!zoomIn && span >= maxSpanLimit - LOGICAL_EDGE_EPS) return;

      if (!zoomIn && span >= maxSpanLimit * 0.85) {
        const chart = chartRef.current;
        if (!chart) return;
        const fitted = fitChartViewportToCandles(chart, n);
        const range = fitted ?? { from: dataMin, to: dataMax };
        navigationBoundsRef.current = captureNavigationBounds(range, n);
        queueViewportStateSyncRef.current(range, null);
        return;
      }

      const factor = zoomIn ? ZOOM_IN_FACTOR : ZOOM_OUT_FACTOR;
      const mid = (current.from + current.to) / 2;
      const targetSpan = span * factor;
      const newSpan = Math.max(minSpan, Math.min(maxSpanLimit, targetSpan));

      let nextFrom = mid - newSpan / 2;
      let nextTo = mid + newSpan / 2;

      // Clamp to bar data while preserving span (zoom can reveal more history than fit margin).
      if (nextFrom < dataMin) {
        nextTo += dataMin - nextFrom;
        nextFrom = dataMin;
      }
      if (nextTo > dataMax) {
        nextFrom -= nextTo - dataMax;
        nextTo = dataMax;
      }
      if (nextFrom < dataMin) {
        nextFrom = dataMin;
        nextTo = Math.min(dataMax, nextFrom + newSpan);
      }
      if (nextTo > dataMax) {
        nextTo = dataMax;
        nextFrom = Math.max(dataMin, nextTo - newSpan);
      }

      const appliedSpan = nextTo - nextFrom;
      if (zoomIn && appliedSpan >= span - 1e-6) return;
      if (!zoomIn && appliedSpan <= span + 1e-6) return;

      setVisibleLogicalRangeOnChart(nextFrom, nextTo);
      navigationBoundsRef.current = maybeExpandNavigationBounds(
        navigationBoundsRef.current,
        { from: nextFrom, to: nextTo },
        n
      );
      queueViewportStateSyncRef.current({ from: nextFrom, to: nextTo }, null);
    },
    [preparedCandles.length, readVisibleLogicalRangeFromChart, setVisibleLogicalRangeOnChart]
  );

  useEffect(() => {
    selectedTradeIdRef.current = selectedTradeId;
    onSelectTradeRef.current = onSelectTrade;
  }, [selectedTradeId, onSelectTrade]);

  // Create chart once.
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Clean up any existing chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      height,
      autoSize: false,
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      handleScale: {
        axisPressedMouseMove: { time: true, price: true },
        mouseWheel: true,
        pinch: true,
      },
      layout: {
        background: { color: bgColor },
        textColor,
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: 12,
      },
      localization: {
        // Keep chart clock aligned with local-time trade list display.
        timeFormatter: formatExecutionChartCrosshairTime,
      } as any,
      grid: {
        vertLines: { color: gridColor },
        horzLines: { color: gridColor },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: gridColor,
        // Prevent auto-shifting when overlay series updates.
        shiftVisibleRangeOnNewBar: false,
        tickMarkFormatter: formatExecutionChartTickTime,
        // Allow dense zoom-out so 1h (and finer) windows can show full loaded history.
        minBarSpacing: MIN_BAR_SPACING_PX,
      },
      rightPriceScale: {
        borderColor: gridColor,
        // Add generous headroom so aboveBar markers stay visible
        // even when the chart is tall.
        scaleMargins: {
          top: 0.35,
          bottom: 0.05,
        },
      },
      crosshair: {
        mode: 1, // Magnet
        vertLine: { color: gridColor, width: 1, style: 2 },
        horzLine: { color: gridColor, width: 1, style: 2 },
      },
    });

    chartRef.current = chart;

    // Regime bgcolor histogram behind candles (full-pane tint via dedicated scale).
    const regimeBackgroundSeries = chart.addSeries(HistogramSeries, {
      priceScaleId: "regime-bg",
      priceLineVisible: false,
      lastValueVisible: false,
      base: 0,
    } as any);
    chart.priceScale("regime-bg").applyOptions({
      visible: false,
      scaleMargins: { top: 0, bottom: 0 },
    });
    regimeBackgroundSeriesRef.current = regimeBackgroundSeries;

    const candleSeries = chart.addSeries(CandlestickSeries);
    candleSeries.applyOptions({
      upColor: "#8E97A6", // Bullish: darker gray
      downColor: "#374151", // Bearish: darker gray
      borderVisible: false,
      wickVisible: true,
      wickUpColor: "#8E97A6",
      wickDownColor: "#374151",
    });
    candleSeriesRef.current = candleSeries;

    // Initialize v5 markers plugin for this series so we can control
    // entry/exit markers via the dedicated API instead of legacy setMarkers.
    try {
      const plugin = createSeriesMarkers(candleSeries);
      markersPluginRef.current = plugin;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ExecutionCandlesChart] Failed to create series markers plugin:", err);
      markersPluginRef.current = null;
    }

    // Cumulative PnL line: green above $0 (mid), red below — no fill so it reads as a line.
    const pnlBackgroundSeries = chart.addSeries(BaselineSeries, {
      baseValue: { type: "price", price: 0 },
      topFillColor1: "rgba(0, 0, 0, 0)",
      topFillColor2: "rgba(0, 0, 0, 0)",
      topLineColor: "#06E363",
      bottomFillColor1: "rgba(0, 0, 0, 0)",
      bottomFillColor2: "rgba(0, 0, 0, 0)",
      bottomLineColor: "#E00101",
      lineWidth: 2,
      lineType: LineType.Curved,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      // Keep this behind overlays/candles as much as possible.
      priceScaleId: "",
    } as any);
    pnlBackgroundSeriesRef.current = pnlBackgroundSeries;

    // Dedicated entry-point series rendered as points only.
    // This allows precise (time, price) plotting from BE values.
    const longEntrySeries = chart.addSeries(LineSeries, {
      color: "#06E363", // Long entry: green
      lineVisible: false as any,
      lineWidth: 1,
      pointMarkersVisible: true,
      pointMarkersRadius: 3,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    } as any);
    longEntrySeriesRef.current = longEntrySeries;
    const longEntryDimSeries = chart.addSeries(LineSeries, {
      color: "rgba(6, 227, 99, 0.5)",
      lineVisible: false as any,
      lineWidth: 1,
      pointMarkersVisible: true,
      pointMarkersRadius: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    } as any);
    longEntryDimSeriesRef.current = longEntryDimSeries;
    const shortEntrySeries = chart.addSeries(LineSeries, {
      color: "#E00101", // Short entry: red
      lineVisible: false as any,
      lineWidth: 1,
      pointMarkersVisible: true,
      pointMarkersRadius: 3,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    } as any);
    shortEntrySeriesRef.current = shortEntrySeries;
    const shortEntryDimSeries = chart.addSeries(LineSeries, {
      color: "rgba(224, 1, 1, 0.5)",
      lineVisible: false as any,
      lineWidth: 1,
      pointMarkersVisible: true,
      pointMarkersRadius: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    } as any);
    shortEntryDimSeriesRef.current = shortEntryDimSeries;

    const longRemovedEntrySeries = chart.addSeries(LineSeries, {
      color: "#A855F7",
      lineVisible: false as any,
      lineWidth: 1,
      pointMarkersVisible: true,
      pointMarkersRadius: 4,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    } as any);
    longRemovedEntrySeriesRef.current = longRemovedEntrySeries;
    const shortRemovedEntrySeries = chart.addSeries(LineSeries, {
      color: "#F97316",
      lineVisible: false as any,
      lineWidth: 1,
      pointMarkersVisible: true,
      pointMarkersRadius: 4,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    } as any);
    shortRemovedEntrySeriesRef.current = shortRemovedEntrySeries;

    // Keep overlays visible when zooming/scrolling: some lightweight-charts/plugin
    // combos can drop markers until re-applied.
    //
    // Subscribe to LOGICAL range only. VisibleTimeRangeChange makes LW call
    // getVisibleRange() internally on every scroll; that throws "Value is null"
    // when the window sits on empty gutters and breaks the chart.
    const timeScale = chart.timeScale();
    const safeVisibleTimeRange = (): VisibleRange => safeGetVisibleRange(timeScale);
    const onVisibleLogicalRangeChange = () => {
      let logicalPayload: { from: number; to: number } | null = null;
      try {
        const logical = timeScale.getVisibleLogicalRange() as {
          from?: number;
          to?: number;
        } | null;
        if (logical && logical.from != null && logical.to != null) {
          logicalPayload = { from: Number(logical.from), to: Number(logical.to) };
        }
      } catch {
        // ignore
      }
      queueViewportStateSyncRef.current(logicalPayload, safeVisibleTimeRange());
      if (markersPluginRef.current) {
        if (markerRefreshRafRef.current != null) cancelAnimationFrame(markerRefreshRafRef.current);
        markerRefreshRafRef.current = requestAnimationFrame(() => {
          markerRefreshRafRef.current = null;
          try {
            markersPluginRef.current?.setMarkers(lastMarkersRef.current as any);
          } catch {
            // ignore
          }
        });
      }
    };
    try {
      timeScale.subscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChange);
    } catch (_) {
      // ignore
    }
    const onChartClick = (param: any) => {
      if (!onSelectTradeRef.current || !candleSeriesRef.current) return;
      const t = param?.time;
      const y = param?.point?.y;
      if (t == null || y == null) return;
      const clickTime = Number(t);
      const clickPrice = candleSeriesRef.current.coordinateToPrice(y);
      if (!Number.isFinite(clickTime) || clickPrice == null || !Number.isFinite(clickPrice)) return;

      const candidates = clickableEntriesRef.current;
      if (!candidates.length) return;

      let bestId: string | null = null;
      let bestScore = Number.POSITIVE_INFINITY;
      for (const c of candidates) {
        const dt = Math.abs(c.time - clickTime);
        const dp = Math.abs(c.price - clickPrice);
        // Weighted distance in (time, price) space.
        const score = dt * 2 + dp;
        if (score < bestScore) {
          bestScore = score;
          bestId = c.tradeId;
        }
      }
      if (!bestId) return;
      // If currently selected trade is clicked again, toggle off.
      onSelectTradeRef.current(bestId === selectedTradeIdRef.current ? null : bestId);
    };
    try {
      chart.subscribeClick(onChartClick);
    } catch (_) {
      // ignore
    }
    // Prime visible range once.
    try {
      onVisibleLogicalRangeChange();
    } catch (_) {
      // ignore
    }

    const rect = chartContainerRef.current.getBoundingClientRect();
    if (rect.width > 0 && height > 0) chart.resize(rect.width, height);
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        const h = Math.floor(entry.contentRect.height);
        if (!chartRef.current || w <= 0 || h <= 0) return;

        const prev = lastSizeRef.current;
        if (prev && prev.w === w && prev.h === h) return;
        lastSizeRef.current = { w, h };

        if (resizeRafRef.current != null) cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = requestAnimationFrame(() => {
          resizeRafRef.current = null;
          if (!chartRef.current) return;
          try {
            // Resize only — never re-fit. Re-fitting at "full zoom" ignored pan offset
            // and yanked the viewport back to center while the user was scrolling.
            chartRef.current.resize(w, h);
          } catch (_) {
            // Chart may be disposed
          }
        });
      }
    });
    resizeObserver.observe(chartContainerRef.current);
    resizeObserverRef.current = resizeObserver;

    setChartVersion((v) => v + 1);

    return () => {
      const ch = chartRef.current;
      chartRef.current = null;
      candleSeriesRef.current = null;
      markersPluginRef.current = null;
      longEntrySeriesRef.current = null;
      shortEntrySeriesRef.current = null;
      longEntryDimSeriesRef.current = null;
      shortEntryDimSeriesRef.current = null;
      longRemovedEntrySeriesRef.current = null;
      shortRemovedEntrySeriesRef.current = null;
      pnlBackgroundSeriesRef.current = null;
      regimeBackgroundSeriesRef.current = null;
      slSegmentSeriesRefs.current = [];
      tpSegmentSeriesRefs.current = [];
      simSlSegmentSeriesRefs.current = [];
      simTpSegmentSeriesRefs.current = [];
      exitSegmentSeriesRefs.current = [];
      pnlZoneSeriesRefs.current = [];
      hasFitOnceRef.current = false;
      lastAutoFitViewportKeyRef.current = "";
      skipViewportRestoreOnceRef.current = false;
      navigationBoundsRef.current = null;
      if (resizeRafRef.current != null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      try {
        ch?.timeScale().unsubscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChange);
      } catch (_) {
        // ignore
      }
      try {
        ch?.unsubscribeClick(onChartClick as any);
      } catch (_) {
        // ignore
      }
      if (ch) {
        try {
          ch.remove();
        } catch (_) {
          // Chart may already be disposed
        }
      }
    };
  }, [height]);

  // Theme changes update chart chrome in place — do not recreate the chart (data overlays stay mounted).
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.applyOptions({
      layout: {
        background: { color: bgColor },
        textColor,
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontSize: 12,
      },
      grid: {
        vertLines: { color: gridColor },
        horzLines: { color: gridColor },
      },
      timeScale: {
        borderColor: gridColor,
      },
      rightPriceScale: {
        borderColor: gridColor,
      },
      crosshair: {
        vertLine: { color: gridColor },
        horzLine: { color: gridColor },
      },
    });
  }, [bgColor, gridColor, textColor]);

  const priceFormat = useMemo(() => resolvePriceFormat(preparedCandles), [preparedCandles]);

  // Update candle data.
  useEffect(() => {
    if (!candleSeriesRef.current || !chartRef.current) return;
    try {
      candleSeriesRef.current.applyOptions({
        priceFormat: {
          type: "price",
          precision: priceFormat.precision,
          minMove: priceFormat.minMove,
        },
      });
      candleSeriesRef.current.setData(preparedCandles as any);
      if (preparedCandles.length > 0 && chartRef.current) {
        const shouldAutoFit =
          candleViewportKey.length > 0 &&
          candleViewportKey !== lastAutoFitViewportKeyRef.current;
        if (shouldAutoFit) {
          lastAutoFitViewportKeyRef.current = candleViewportKey;
          hasFitOnceRef.current = true;
          const n = preparedCandles.length;
          const runFit = () => {
            if (!chartRef.current) return;
            const fitted = fitChartViewportToCandles(chartRef.current, n);
            skipViewportRestoreOnceRef.current = true;
            if (fitted) {
              navigationBoundsRef.current = captureNavigationBounds(fitted, n);
              queueViewportStateSyncRef.current(fitted, null);
            }
          };
          if (n >= MIN_VISIBLE_BARS) {
            requestAnimationFrame(() => requestAnimationFrame(runFit));
          } else {
            runFit();
          }
        } else if (!navigationBoundsRef.current) {
          try {
            const logical = chartRef.current.timeScale().getVisibleLogicalRange();
            if (logical) {
              navigationBoundsRef.current = captureNavigationBounds(
                { from: logical.from, to: logical.to },
                preparedCandles.length
              );
            }
          } catch {
            // ignore
          }
        }
      } else {
        hasFitOnceRef.current = false;
        lastAutoFitViewportKeyRef.current = "";
        navigationBoundsRef.current = null;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ExecutionCandlesChart] Failed to update markers:", err);
    }
  }, [candleViewportKey, preparedCandles, priceFormat, chartVersion]);

  // Update cumulative PnL change line (green above $0, red below).
  useEffect(() => {
    const series = pnlBackgroundSeriesRef.current;
    if (!series) return;
    if (!showPnlBackgroundOverlay) {
      series.setData([]);
      return;
    }
    if (!preparedCandles.length) {
      series.setData([]);
      return;
    }

    const pnlByTime = new Map<number, number>();
    const policyOverlayActive = hasPolicyOverlay;
    const overlayAdjustsPnl = policyOverlayActive || simulationLineOverlay;
    for (const t of visibleTrades) {
      const realizeAt = resolveTradePnlRealizationTime(t);
      if (!realizeAt) continue;
      const rawTs = toUtcTimestampSeconds(realizeAt);
      const ts = snapToContainingCandleTime(preparedCandles, rawTs);
      if (!ts) continue;
      let pnl: number | null = null;
      if (overlayAdjustsPnl) {
        // Removed / removed winners|losers: chart is removed-only — don't drop their PnL
        // (policyOverlayActive would otherwise null every point and flatten the band).
        pnl = resolveOverlayAdjustedTradePnl(t, {
          policyOverlayActive: policyOverlayActive && !includeRemovedInPnl,
          simulationLineOverlay,
        });
      } else if (t.net_pnl != null) {
        pnl = Number(t.net_pnl);
      } else if (t.entry_price != null && t.exit_price != null) {
        pnl =
          t.direction === "short"
            ? Number(t.entry_price) - Number(t.exit_price)
            : Number(t.exit_price) - Number(t.entry_price);
      }
      if (pnl == null) continue;
      pnlByTime.set(Number(ts), (pnlByTime.get(Number(ts)) ?? 0) + pnl);
    }

    // Build cumulative signal.
    let running = 0;
    const cumulative = preparedCandles.map((c) => {
      running += pnlByTime.get(Number(c.time)) ?? 0;
      return { time: Number(c.time), signal: running };
    });

    let minPrice = Number.POSITIVE_INFINITY;
    let maxPrice = Number.NEGATIVE_INFINITY;
    for (const c of preparedCandles) {
      if (c.low < minPrice) minPrice = c.low;
      if (c.high > maxPrice) maxPrice = c.high;
    }
    const midPrice = (minPrice + maxPrice) / 2;
    // Wider band + robust scaling (see mapCumulativePnlToBaselineBand) → clearer micro swings.
    const halfBand = Math.max(1e-6, (maxPrice - minPrice) * 0.4);

    const data = mapCumulativePnlToBaselineBand(cumulative, midPrice, halfBand).map((p) => ({
      time: p.time as unknown as Time,
      value: p.value,
    }));

    series.applyOptions({
      baseValue: { type: "price", price: midPrice },
    } as any);
    series.setData(data as any);
  }, [
    preparedCandles,
    showPnlBackgroundOverlay,
    visibleTrades,
    hasPolicyOverlay,
    includeRemovedInPnl,
    hasBucketHighlight,
    simulationLineOverlay,
  ]);

  // Market-condition bgcolor histogram (aligned with prepared candles).
  useEffect(() => {
    const series = regimeBackgroundSeriesRef.current;
    if (!series) return;
    const hasConditions = (regimeOverlay?.conditions?.length ?? 0) > 0;
    if (!showRegimeOverlay || !hasConditions || !preparedRegimeBars.length) {
      series.setData([]);
      return;
    }
    const data = buildRegimeHistogramData(preparedRegimeBars).map((p) => ({
      time: p.time as unknown as Time,
      value: p.value,
      color: p.color,
    }));
    series.setData(data as any);
  }, [preparedRegimeBars, regimeOverlay?.conditions?.length, showRegimeOverlay]);

  // Update markers and SL/TP segments.
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const markersPlugin = markersPluginRef.current;
    const longEntrySeries = longEntrySeriesRef.current;
    const shortEntrySeries = shortEntrySeriesRef.current;
    const longEntryDimSeries = longEntryDimSeriesRef.current;
    const shortEntryDimSeries = shortEntryDimSeriesRef.current;
    const longRemovedEntrySeries = longRemovedEntrySeriesRef.current;
    const shortRemovedEntrySeries = shortRemovedEntrySeriesRef.current;
    if (
      !candleSeries ||
      !longEntrySeries ||
      !shortEntrySeries ||
      !longEntryDimSeries ||
      !shortEntryDimSeries ||
      !longRemovedEntrySeries ||
      !shortRemovedEntrySeries ||
      !chartRef.current ||
      !markersPlugin
    )
      return;

    const markers: SeriesMarker<Time>[] = [];
    const longEntryData: LineData[] = [];
    const shortEntryData: LineData[] = [];
    const longEntryDimData: LineData[] = [];
    const shortEntryDimData: LineData[] = [];
    const longRemovedEntryData: LineData[] = [];
    const shortRemovedEntryData: LineData[] = [];

    const currentVisibleRange =
      visibleRange ?? safeGetVisibleRange(chartRef.current.timeScale());
    const rangeFrom = currentVisibleRange?.from ?? null;
    const rangeTo = currentVisibleRange?.to ?? null;
    // Guard against transient invalid ranges during zoom transitions.
    if (rangeFrom != null && rangeTo != null && rangeTo < rangeFrom) return;
    const timeScale = chartRef.current.timeScale();
    const preservedLogicalRange = timeScale.getVisibleLogicalRange() as any;
    const clickableEntries: Array<{ tradeId: string; time: number; price: number }> = [];

    if (useBucketedEntryMarkers) {
      // Overview: bucket entries by candle time; SL/TP only for an explicitly selected trade.
      const buckets = new Map<number, CohortBucketCounts>();
      const policyBuckets = new Map<number, PolicyBucketCounts>();
      const selectedTrade =
        selectedTradeId != null ? visibleTrades.find((t) => t.trade_id === selectedTradeId) : null;
      const selectedRawTs = selectedTrade?.entry_time ? toUtcTimestampSeconds(selectedTrade.entry_time) : null;
      const selectedEntryTs = snapToNearestCandleTime(preparedCandles, selectedRawTs);
      const selectedPolicyTag = (selectedTrade as ExecutionTradeOverlay | undefined)?.policy_overlay;
      for (const t of visibleTrades) {
        if (!t.entry_time) continue;
        const rawEntryTs = toUtcTimestampSeconds(t.entry_time);
        const rawExitTs = t.exit_time ? toUtcTimestampSeconds(t.exit_time) : null;
        const entryPlot = resolveEntryPlotOnCandles(preparedCandles, rawEntryTs, rawExitTs);
        if (!entryPlot) continue;
        const entryTs = entryPlot.time as UTCTimestamp;
        if (rangeFrom != null && rangeTo != null && (entryTs < rangeFrom || entryTs > rangeTo)) continue;
        if (t.trade_id && t.entry_price != null) {
          clickableEntries.push({
            tradeId: t.trade_id,
            time: Number(entryTs),
            price: Number(t.entry_price),
          });
        }
        const key = Number(entryTs);
        // Selected trade is drawn full-opacity separately so overview dimming
        // never fades or downsizes the active selection.
        if (selectedTradeId && t.trade_id === selectedTradeId) continue;
        // Outcome arrows (Long Win / Short Loss / …) for cohort preview and Trade Log default.
        // Policy kept/removed still uses the green/red count circles + removed labels.
        if (hasBucketHighlight || !hasPolicyOverlay) {
          const prev = buckets.get(key) ?? emptyCohortBucketCounts();
          accumulateCohortBucketTrade(prev, t);
          buckets.set(key, prev);
          continue;
        }
        const prev = policyBuckets.get(key) ?? emptyPolicyBucketCounts();
        accumulatePolicyBucketTrade(prev, t);
        policyBuckets.set(key, prev);
      }

      const dimOverviewForSelection = selectedTradeId != null;
      if (hasBucketHighlight || !hasPolicyOverlay) {
        buckets.forEach((bucket, timeNum) => {
          pushCohortBucketMarkers(markers, timeNum, bucket, dimOverviewForSelection);
        });
        if (selectedTrade && selectedEntryTs != null) {
          const dir = selectedTrade.direction === "short" ? "short" : "long";
          const outcome = resolveTradeCohortOutcome(selectedTrade);
          if (selectedTrade.bucket_overlay === "out_of_bucket") {
            pushCountMarker(
              markers,
              Number(selectedEntryTs),
              1,
              COHORT_PREVIEW_OTHER_RGBA,
              "circle",
              1,
            );
          } else {
            pushLabeledOutcomeMarker(
              markers,
              Number(selectedEntryTs),
              dir,
              outcome,
              1,
              2,
              false,
            );
          }
        }
      } else {
        policyBuckets.forEach((bucket, timeNum) => {
          pushPolicyBucketMarkers(markers, timeNum, bucket, {
            dimUnselected: dimOverviewForSelection,
          });
        });
        if (selectedTrade && selectedEntryTs != null) {
          const dir = selectedTrade.direction === "short" ? "short" : "long";
          if (selectedPolicyTag === "kept") {
            pushCountMarker(
              markers,
              Number(selectedEntryTs),
              1,
              dir === "short" ? "rgba(224, 1, 1, 0.75)" : "rgba(6, 227, 99, 0.75)",
              "circle",
              1,
            );
          } else if (selectedPolicyTag === "removed") {
            pushLabeledOutcomeMarker(
              markers,
              Number(selectedEntryTs),
              dir,
              resolveTradeCohortOutcome(selectedTrade),
              1,
              2,
              false,
            );
          }
        }
      }
      // Hide precise entry dots in overview mode.
      longEntrySeries.setData([]);
      shortEntrySeries.setData([]);
      longEntryDimSeries.setData([]);
      shortEntryDimSeries.setData([]);
      longRemovedEntrySeries.setData([]);
      shortRemovedEntrySeries.setData([]);
      lastLongEntryDataRef.current = [];
      lastShortEntryDataRef.current = [];
      for (const s of slSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      slSegmentSeriesRefs.current = [];
      for (const s of tpSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      tpSegmentSeriesRefs.current = [];
      for (const s of simSlSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      simSlSegmentSeriesRefs.current = [];
      for (const s of simTpSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      simTpSegmentSeriesRefs.current = [];
      for (const s of exitSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      exitSegmentSeriesRefs.current = [];
      for (const s of pnlZoneSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      pnlZoneSeriesRefs.current = [];

      // If a trade is selected while zoomed out, overlay that single trade in detailed style
      // without removing the overview circles.
      if (selectedTrade && selectedTrade.entry_time && selectedTrade.entry_price != null) {
        const rawEntryTs = toUtcTimestampSeconds(selectedTrade.entry_time);
        const rawExitForPlot = selectedTrade.exit_time
          ? toUtcTimestampSeconds(selectedTrade.exit_time)
          : null;
        const entryPlot = resolveEntryPlotOnCandles(preparedCandles, rawEntryTs, rawExitForPlot);
        if (entryPlot) {
          const entryTs = entryPlot.time as UTCTimestamp;
          const makeSegment = (
            price: number | null,
            color: string
          ): ISeriesApi<"Line"> | null => {
            if (price == null) return null;
            const rawExitTs = selectedTrade.exit_time ? toUtcTimestampSeconds(selectedTrade.exit_time) : null;
            const snappedExitTs = snapToNearestCandleTime(preparedCandles, rawExitTs);
            const fallbackEnd = (entryTs + Math.max(1, secondsPerCandle * 2)) as UTCTimestamp;
            let endTs = snappedExitTs ?? fallbackEnd;
            if (endTs < entryTs || endTs === entryTs) endTs = fallbackEnd;
            if (candleMinTime != null && endTs < candleMinTime) endTs = candleMinTime;
            if (candleMaxTime != null && endTs > candleMaxTime) endTs = candleMaxTime;
            const startTs = candleMinTime != null ? (entryTs < candleMinTime ? candleMinTime : entryTs) : entryTs;
            if (endTs < startTs) return null;
            try {
              const s = chartRef.current!.addSeries(LineSeries, {
                color,
                lineWidth: 2,
                priceLineVisible: false,
                lastValueVisible: false,
                crosshairMarkerVisible: false,
              } as any);
              s.setData(
                [
                  { time: startTs as unknown as Time, value: Number(price) },
                  { time: endTs as unknown as Time, value: Number(price) },
                ] as any
              );
              return s;
            } catch (_) {
              return null;
            }
          };

          const selectedLevels = resolveOverlaySlTpLevels(selectedTrade);
          const sl = makeSegment(selectedLevels.slPrice, SL_COLOR);
          const tp = makeSegment(selectedLevels.tpPrice, TP_COLOR);
          const selectedSim =
            selectedTradeId != null
              ? changedSimulatedTrades.find((t) => t.trade_id === selectedTradeId)
              : undefined;
          const exitSeg = simulationLineOverlay
            ? resolveActualExitOverlaySegment(selectedTrade)
            : resolveOverlayExitSegment(selectedTrade);
          const exitLine = exitSeg ? makeSegment(exitSeg.price, exitSeg.color) : null;
          if (sl) slSegmentSeriesRefs.current.push(sl);
          if (tp) tpSegmentSeriesRefs.current.push(tp);
          if (exitLine) exitSegmentSeriesRefs.current.push(exitLine);

          if (selectedSim) {
            const makeSimSegment = (
              price: number | null,
              color: string
            ): ISeriesApi<"Line"> | null => {
              if (price == null) return null;
              const rawExitTs = selectedSim.exit_time
                ? toUtcTimestampSeconds(selectedSim.exit_time)
                : null;
              const snappedExitTs = snapToNearestCandleTime(preparedCandles, rawExitTs);
              const fallbackEnd = (entryTs + Math.max(1, secondsPerCandle * 2)) as UTCTimestamp;
              let endTs = snappedExitTs ?? fallbackEnd;
              if (endTs < entryTs || endTs === entryTs) endTs = fallbackEnd;
              if (candleMinTime != null && endTs < candleMinTime) endTs = candleMinTime;
              if (candleMaxTime != null && endTs > candleMaxTime) endTs = candleMaxTime;
              const startTs =
                candleMinTime != null ? (entryTs < candleMinTime ? candleMinTime : entryTs) : entryTs;
              if (endTs < startTs) return null;
              try {
                const s = chartRef.current!.addSeries(LineSeries, {
                  color,
                  lineWidth: 2,
                  lineStyle: 2,
                  priceLineVisible: false,
                  lastValueVisible: false,
                  crosshairMarkerVisible: false,
                } as any);
                s.setData(
                  [
                    { time: startTs as unknown as Time, value: Number(price) },
                    { time: endTs as unknown as Time, value: Number(price) },
                  ] as any
                );
                return s;
              } catch (_) {
                return null;
              }
            };
            const simLevels = simulatedSlTpLevels(selectedSim, simulatedLegs);
            const simSl = makeSimSegment(simLevels.slPrice, SL_COLOR);
            const simTp = makeSimSegment(simLevels.tpPrice, TP_COLOR);
            if (simSl) simSlSegmentSeriesRefs.current.push(simSl);
            if (simTp) simTpSegmentSeriesRefs.current.push(simTp);
          }
        }
      }
    } else {
      // Detailed mode: render precise entries and SL/TP/exit levels.
      type LevelSegment = {
        start: UTCTimestamp;
        end: UTCTimestamp;
        price: number;
        selected: boolean;
        color: string;
      };
      const slSegments: LevelSegment[] = [];
      const tpSegments: LevelSegment[] = [];
      const exitSegments: LevelSegment[] = [];
      for (const t of visibleTrades) {
        if (!t.entry_time || t.entry_price == null) continue;
        const rawEntryTs = toUtcTimestampSeconds(t.entry_time);
        const rawExitForPlot = t.exit_time ? toUtcTimestampSeconds(t.exit_time) : null;
        const entryPlot = resolveEntryPlotOnCandles(preparedCandles, rawEntryTs, rawExitForPlot);
        if (!entryPlot) continue;
        const entryTs = entryPlot.time as UTCTimestamp;
        if (candleMinTime != null && candleMaxTime != null) {
          // Keep entry points inside candle domain to avoid time-scale jumps.
          if (entryTs < candleMinTime || entryTs > candleMaxTime) continue;
        }
        if (rangeFrom != null && rangeTo != null && (entryTs < rangeFrom || entryTs > rangeTo)) continue;

        const plottedEntryPrice = entryPlot.beforeSeries
          ? Number(t.entry_price)
          : clampToCandleRange(candleByTime.get(Number(entryTs)), Number(t.entry_price));
        const point = {
          time: entryTs as unknown as Time,
          value: plottedEntryPrice,
        };
        if (t.trade_id) {
          clickableEntries.push({
            tradeId: t.trade_id,
            time: Number(entryTs),
            price: plottedEntryPrice,
          });
        }
        const isSelectedTrade = !!selectedTradeId && !!t.trade_id && t.trade_id === selectedTradeId;
        const isRemoved = t.policy_overlay === "removed";
        const isOutOfBucket = hasBucketHighlight && t.bucket_overlay === "out_of_bucket";
        const isUnchangedSimTrade =
          hasSimulationLayers &&
          changedSimulatedTrades.length > 0 &&
          !!t.trade_id &&
          !changedSimulatedTradeIds.has(t.trade_id);
        const shouldDim =
          (!!selectedTradeId && !isSelectedTrade) ||
          (isOutOfBucket && !isSelectedTrade) ||
          (isUnchangedSimTrade && !isSelectedTrade);
        if (isRemoved) {
          const dir = t.direction === "short" ? "short" : "long";
          const outcome = resolveTradeCohortOutcome(t);
          pushEntryPointData(point, t.direction, shouldDim, {
            longEntryData,
            shortEntryData,
            longEntryDimData,
            shortEntryDimData,
          });
          pushLabeledOutcomeMarker(
            markers,
            Number(entryTs),
            dir,
            outcome,
            1,
            isSelectedTrade ? 2 : shouldDim ? 0 : 1,
            !!selectedTradeId && !isSelectedTrade,
          );
        } else if (hasBucketHighlight && t.bucket_overlay === "in_bucket") {
          const dir = t.direction === "short" ? "short" : "long";
          const outcome = resolveTradeCohortOutcome(t);
          pushEntryPointData(point, t.direction, shouldDim, {
            longEntryData,
            shortEntryData,
            longEntryDimData,
            shortEntryDimData,
          });
          pushLabeledOutcomeMarker(
            markers,
            Number(entryTs),
            dir,
            outcome,
            1,
            isSelectedTrade ? 2 : shouldDim ? 0 : 1,
            !!selectedTradeId && !isSelectedTrade,
          );
        } else if (hasBucketHighlight && t.bucket_overlay === "out_of_bucket") {
          markers.push({
            time: entryTs as unknown as Time,
            position: "inBar",
            color:
              selectedTradeId && !isSelectedTrade
                ? "rgba(148, 163, 184, 0.5)"
                : COHORT_PREVIEW_OTHER_RGBA,
            shape: "circle" as any,
            size: isSelectedTrade ? 2 : 1,
            zOrder: "top",
          } as any);
        } else if (hasPolicyOverlay) {
          if (t.direction === "short") {
            if (shouldDim) shortEntryDimData.push(point);
            else shortEntryData.push(point);
          } else if (shouldDim) {
            longEntryDimData.push(point);
          } else {
            longEntryData.push(point);
          }
        } else {
          const dir = t.direction === "short" ? "short" : "long";
          const outcome = resolveTradeCohortOutcome(t);
          pushEntryPointData(point, t.direction, shouldDim, {
            longEntryData,
            shortEntryData,
            longEntryDimData,
            shortEntryDimData,
          });
          pushLabeledOutcomeMarker(
            markers,
            Number(entryTs),
            dir,
            outcome,
            1,
            isSelectedTrade ? 2 : shouldDim ? 0 : 1,
            !!selectedTradeId && !isSelectedTrade,
          );
        }

        const pushLevelSegment = (price: number | null, color: string, bucket: LevelSegment[]) => {
          if (price == null) return;
          const rawExitTs = t.exit_time ? toUtcTimestampSeconds(t.exit_time) : null;
          const snappedExitTs = snapToNearestCandleTime(preparedCandles, rawExitTs);
          const fallbackEnd = (entryTs + Math.max(1, secondsPerCandle * 2)) as UTCTimestamp;
          let endTs = snappedExitTs ?? fallbackEnd;
          if (endTs < entryTs) endTs = fallbackEnd;
          if (endTs === entryTs) endTs = fallbackEnd;
          if (candleMinTime != null && endTs < candleMinTime) endTs = candleMinTime;
          if (candleMaxTime != null && endTs > candleMaxTime) endTs = candleMaxTime;
          const startTs =
            candleMinTime != null ? (entryTs < candleMinTime ? candleMinTime : entryTs) : entryTs;
          if (endTs >= startTs) {
            bucket.push({
              start: startTs,
              end: endTs,
              price,
              selected: !shouldDim,
              color,
            });
          }
        };

        const useDefaultOutcomeMarkers = !hasBucketHighlight && !hasPolicyOverlay;
        const showLevelsForTrade = simulationLineOverlay
          ? !!selectedTradeId && t.trade_id === selectedTradeId
          : useDefaultOutcomeMarkers
            ? isSelectedTrade
            : true;
        if (showLevelsForTrade) {
          const levels = resolveOverlaySlTpLevels(t);
          pushLevelSegment(levels.slPrice, SL_COLOR, slSegments);
          pushLevelSegment(levels.tpPrice, TP_COLOR, tpSegments);
          if (simulationLineOverlay && t.trade_id && changedSimulatedTradeIds.has(t.trade_id)) {
            const actualExit = resolveActualExitOverlaySegment(t);
            if (actualExit) pushLevelSegment(actualExit.price, actualExit.color, exitSegments);
          } else {
            const exitSeg = resolveOverlayExitSegment(t);
            if (exitSeg) pushLevelSegment(exitSeg.price, exitSeg.color, exitSegments);
          }
        }
      }

      const simSlSegments: LevelSegment[] = [];
      const simTpSegments: LevelSegment[] = [];
      for (const sim of changedSimulatedTrades) {
        if (simulationLineOverlay && selectedTradeId && sim.trade_id !== selectedTradeId) continue;
        const baseline = sim.trade_id ? baselineTradeById.get(sim.trade_id) : undefined;
        const entryTrade = baseline ?? sim;
        if (!entryTrade.entry_time || entryTrade.entry_price == null) continue;
        const rawEntryTs = toUtcTimestampSeconds(entryTrade.entry_time);
        const rawExitForPlot = entryTrade.exit_time
          ? toUtcTimestampSeconds(entryTrade.exit_time)
          : sim.exit_time
            ? toUtcTimestampSeconds(sim.exit_time)
            : null;
        const entryPlot = resolveEntryPlotOnCandles(preparedCandles, rawEntryTs, rawExitForPlot);
        if (!entryPlot) continue;
        const entryTs = entryPlot.time as UTCTimestamp;
        if (candleMinTime != null && candleMaxTime != null) {
          if (entryTs < candleMinTime || entryTs > candleMaxTime) continue;
        }
        if (rangeFrom != null && rangeTo != null && (entryTs < rangeFrom || entryTs > rangeTo)) continue;

        const isSelectedTrade =
          !!selectedTradeId && !!sim.trade_id && sim.trade_id === selectedTradeId;
        const shouldDimSim = !!selectedTradeId && !isSelectedTrade;

        const pushSimLevelSegment = (price: number | null, color: string, bucket: LevelSegment[]) => {
          if (price == null) return;
          const rawExitTs = sim.exit_time ? toUtcTimestampSeconds(sim.exit_time) : null;
          const snappedExitTs = snapToNearestCandleTime(preparedCandles, rawExitTs);
          const fallbackEnd = (entryTs + Math.max(1, secondsPerCandle * 2)) as UTCTimestamp;
          let endTs = snappedExitTs ?? fallbackEnd;
          if (endTs < entryTs) endTs = fallbackEnd;
          if (endTs === entryTs) endTs = fallbackEnd;
          if (candleMinTime != null && endTs < candleMinTime) endTs = candleMinTime;
          if (candleMaxTime != null && endTs > candleMaxTime) endTs = candleMaxTime;
          const startTs =
            candleMinTime != null ? (entryTs < candleMinTime ? candleMinTime : entryTs) : entryTs;
          if (endTs >= startTs) {
            bucket.push({
              start: startTs,
              end: endTs,
              price,
              selected: !shouldDimSim,
              color,
            });
          }
        };

        const simLevels = simulatedSlTpLevels(sim, simulatedLegs);
        pushSimLevelSegment(simLevels.slPrice, SL_COLOR, simSlSegments);
        pushSimLevelSegment(simLevels.tpPrice, TP_COLOR, simTpSegments);
      }

      // lightweight-charts requires strictly ascending unique time order for setData.
      // If multiple trades map to same candle timestamp, spread them by tiny
      // intra-candle offsets so all entries remain visible.
      const sortAndSpread = (points: LineData[]): LineData[] => {
        const sorted = [...points].sort((a, b) => Number(a.time) - Number(b.time));
        const spread: LineData[] = [];
        let i = 0;
        while (i < sorted.length) {
          const baseTime = Number(sorted[i].time);
          let j = i + 1;
          while (j < sorted.length && Number(sorted[j].time) === baseTime) j += 1;
          const groupSize = j - i;
          if (groupSize === 1) {
            spread.push(sorted[i]);
          } else {
            const nextDistinctTime =
              j < sorted.length ? Number(sorted[j].time) : baseTime + secondsPerCandle;
            const gap = Math.max(1, nextDistinctTime - baseTime);
            const maxForward = Math.min(Math.max(0, secondsPerCandle - 1), gap - 1);
            if (maxForward <= 0) {
              spread.push(sorted[i]);
            } else {
              const step =
                groupSize > 1 ? Math.max(1, Math.floor(maxForward / (groupSize - 1))) : 0;
              for (let k = 0; k < groupSize; k += 1) {
                const shiftedTime = (baseTime + Math.min(k * step, maxForward)) as unknown as Time;
                spread.push({
                  ...sorted[i + k],
                  time: shiftedTime,
                });
              }
            }
          }
          i = j;
        }
        return spread;
      };
      const dedupedLongEntryData = sortAndSpread(longEntryData);
      const dedupedShortEntryData = sortAndSpread(shortEntryData);
      const dedupedLongEntryDimData = sortAndSpread(longEntryDimData);
      const dedupedShortEntryDimData = sortAndSpread(shortEntryDimData);
      const dedupedLongRemovedEntryData = sortAndSpread(longRemovedEntryData);
      const dedupedShortRemovedEntryData = sortAndSpread(shortRemovedEntryData);
      for (const s of slSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      slSegmentSeriesRefs.current = [];
      for (const s of tpSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      tpSegmentSeriesRefs.current = [];
      for (const s of simSlSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      simSlSegmentSeriesRefs.current = [];
      for (const s of simTpSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      simTpSegmentSeriesRefs.current = [];
      for (const s of exitSegmentSeriesRefs.current) {
        try {
          chartRef.current?.removeSeries(s);
        } catch (_) {
          // ignore disposed series
        }
      }
      exitSegmentSeriesRefs.current = [];

      const levelSegmentStroke = (color: string, selected: boolean) => {
        if (simulationLineOverlay) return color;
        if (selected) return color;
        if (color === SL_COLOR) return "rgba(224, 1, 1, 0.5)";
        if (color === TP_COLOR) return "rgba(6, 227, 99, 0.5)";
        return color;
      };

      const addLevelSegmentSeries = (
        segments: LevelSegment[],
        refs: React.MutableRefObject<Array<ISeriesApi<"Line">>>,
        dashed = false
      ) => {
        const sorted = [...segments].sort((a, b) => a.start - b.start);
        for (const seg of sorted) {
          if (rangeFrom != null && rangeTo != null && (seg.end < rangeFrom || seg.start > rangeTo)) continue;
          const clippedStart = rangeFrom != null ? (seg.start < rangeFrom ? rangeFrom : seg.start) : seg.start;
          const clippedEnd = rangeTo != null ? (seg.end > rangeTo ? rangeTo : seg.end) : seg.end;
          if (clippedEnd < clippedStart) continue;
          try {
            const series = chartRef.current!.addSeries(LineSeries, {
              color: levelSegmentStroke(seg.color, seg.selected),
              lineWidth: simulationLineOverlay ? (seg.selected ? 3 : 2) : seg.selected ? 2 : 1,
              lineStyle: dashed ? 2 : 0,
              priceLineVisible: false,
              lastValueVisible: false,
              crosshairMarkerVisible: false,
            } as any);
            series.setData(
              [
                { time: clippedStart as unknown as Time, value: seg.price },
                { time: clippedEnd as unknown as Time, value: seg.price },
              ] as any
            );
            refs.current.push(series);
          } catch (_) {
            // ignore individual segment failures
          }
        }
      };

      addLevelSegmentSeries(slSegments, slSegmentSeriesRefs);
      addLevelSegmentSeries(tpSegments, tpSegmentSeriesRefs);
      addLevelSegmentSeries(simSlSegments, simSlSegmentSeriesRefs, true);
      addLevelSegmentSeries(simTpSegments, simTpSegmentSeriesRefs, true);
      const sortedExitSegments = [...exitSegments].sort((a, b) => a.start - b.start);
      for (const seg of sortedExitSegments) {
        if (rangeFrom != null && rangeTo != null && (seg.end < rangeFrom || seg.start > rangeTo)) continue;
        const clippedStart = rangeFrom != null ? (seg.start < rangeFrom ? rangeFrom : seg.start) : seg.start;
        const clippedEnd = rangeTo != null ? (seg.end > rangeTo ? rangeTo : seg.end) : seg.end;
        if (clippedEnd < clippedStart) continue;
        try {
          const exitSeries = chartRef.current.addSeries(LineSeries, {
            color: levelSegmentStroke(seg.color, seg.selected),
            lineWidth: seg.selected ? 2 : 1,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          } as any);
          exitSeries.setData(
            [
              { time: clippedStart as unknown as Time, value: seg.price },
              { time: clippedEnd as unknown as Time, value: seg.price },
            ] as any
          );
          exitSegmentSeriesRefs.current.push(exitSeries);
        } catch (_) {
          // ignore individual segment failures
        }
      }
      // Plot precise entry points from BE values.
      try {
        longEntrySeries.setData(dedupedLongEntryData as any);
        shortEntrySeries.setData(dedupedShortEntryData as any);
        longEntryDimSeries.setData(dedupedLongEntryDimData as any);
        shortEntryDimSeries.setData(dedupedShortEntryDimData as any);
        longRemovedEntrySeries.setData(dedupedLongRemovedEntryData as any);
        shortRemovedEntrySeries.setData(dedupedShortRemovedEntryData as any);
        lastLongEntryDataRef.current = dedupedLongEntryData;
        lastShortEntryDataRef.current = dedupedShortEntryData;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ExecutionCandlesChart] Failed to set entry overlay data:", err);
      }
    }

    if (hasSimulationLayers && !simulationLineOverlay) {
      for (const t of changedSimulatedTrades) {
        if (!t.exit_time) continue;
        const rawExitTs = toUtcTimestampSeconds(t.exit_time);
        const exitTs = snapToNearestCandleTime(preparedCandles, rawExitTs);
        if (!exitTs) continue;
        if (rangeFrom != null && rangeTo != null && (exitTs < rangeFrom || exitTs > rangeTo)) continue;
        markers.push({
          time: Number(exitTs) as unknown as Time,
          position: "belowBar",
          color: SIMULATED_EXIT_MARKER_COLOR,
          shape: "circle" as any,
          size: isZoomedIn ? 2 : 1,
          text: "Sim",
          zOrder: "top",
        } as any);
      }

      for (const evt of visibleManagementEvents) {
        if (simulationLineOverlay) continue;
        const { timeRaw, price, label } = parseManagementEventFields(evt);
        const rawTs =
          typeof timeRaw === "string" || typeof timeRaw === "number"
            ? toUtcTimestampSeconds(String(timeRaw))
            : null;
        const eventTs = snapToNearestCandleTime(preparedCandles, rawTs);
        if (!eventTs) continue;
        if (rangeFrom != null && rangeTo != null && (eventTs < rangeFrom || eventTs > rangeTo)) continue;
        const tip = label.length > 28 ? `${label.slice(0, 27)}…` : label;
        markers.push({
          time: Number(eventTs) as unknown as Time,
          position: price != null ? "inBar" : "aboveBar",
          color: MANAGEMENT_EVENT_MARKER_COLOR,
          shape: "square" as any,
          size: 1,
          text: tip,
          zOrder: "top",
        } as any);
      }
    }

    clickableEntriesRef.current = clickableEntries;

    try {
      // Use the v5 markers plugin API so markers render correctly in
      // the installed lightweight-charts version.
      markersPlugin.setMarkers(markers as any);
      lastMarkersRef.current = markers;
      if (useBucketedEntryMarkers) {
        // Overview: markers carry bucketed activity; entry line series stay empty
        // except a single selected trade (re-applied after clear).
        longEntrySeries.setData([]);
        shortEntrySeries.setData([]);
        longEntryDimSeries.setData([]);
        shortEntryDimSeries.setData([]);
        longRemovedEntrySeries.setData([]);
        shortRemovedEntrySeries.setData([]);
        lastLongEntryDataRef.current = [];
        lastShortEntryDataRef.current = [];

        const sel =
          selectedTradeId != null ? visibleTrades.find((t) => t.trade_id === selectedTradeId) : null;
        if (sel?.entry_time && sel.entry_price != null) {
          const rawSelTs = toUtcTimestampSeconds(sel.entry_time);
          const rawSelExit = sel.exit_time ? toUtcTimestampSeconds(sel.exit_time) : null;
          const selPlot = resolveEntryPlotOnCandles(preparedCandles, rawSelTs, rawSelExit);
          if (selPlot) {
            const selEntryTs = selPlot.time as UTCTimestamp;
            const selectedPoint: LineData = {
              time: selEntryTs as unknown as Time,
              value: selPlot.beforeSeries
                ? Number(sel.entry_price)
                : clampToCandleRange(
                    candleByTime.get(Number(selEntryTs)),
                    Number(sel.entry_price)
                  ),
            };
            const selRemoved = sel.policy_overlay === "removed";
            const useOutcomeSelectionMarker =
              selRemoved || hasBucketHighlight || !hasPolicyOverlay;
            if (useOutcomeSelectionMarker) {
              if (sel.direction === "short") {
                shortEntrySeries.setData([selectedPoint] as any);
              } else {
                longEntrySeries.setData([selectedPoint] as any);
              }
            } else if (sel.direction === "short") {
              shortEntrySeries.setData([selectedPoint] as any);
            } else {
              longEntrySeries.setData([selectedPoint] as any);
            }
          }
        }
      }

      const preservedSpan =
        preservedLogicalRange.to != null && preservedLogicalRange.from != null
          ? Number(preservedLogicalRange.to) - Number(preservedLogicalRange.from)
          : 0;
      const staleNarrowViewport =
        preparedCandles.length >= MIN_VISIBLE_BARS &&
        preservedSpan > 0 &&
        preservedSpan < fullChartZoomSpan(preparedCandles.length) * 0.95;

      // Prevent unexpected left/right jumps when overlay mode changes.
      // Keep current viewport unless an explicit focus-pan is requested.
      if (
        !focusTradeId &&
        !skipViewportRestoreOnceRef.current &&
        !staleNarrowViewport &&
        preservedLogicalRange?.from != null &&
        preservedLogicalRange?.to != null
      ) {
        try {
          timeScale.setVisibleLogicalRange({
            from: Number(preservedLogicalRange.from),
            to: Number(preservedLogicalRange.to),
          });
        } catch (_) {
          // ignore viewport restore errors
        }
      }
      skipViewportRestoreOnceRef.current = false;

    } catch (_) {
      // Chart may be disposed (e.g. unmounted on account switch)
    }
  }, [
    preparedCandles,
    selectedTradeId,
    visibleTrades,
    visibleRange,
    isZoomedIn,
    useBucketedEntryMarkers,
    candleMinTime,
    candleMaxTime,
    focusTradeId,
    hasPolicyOverlay,
    hasBucketHighlight,
    secondsPerCandle,
    hasSimulationLayers,
    changedSimulatedTrades,
    changedSimulatedTradeIds,
    baselineTradeById,
    simulationLineOverlay,
    visibleManagementEvents,
    chartVersion,
  ]);

  // If a trade is selected from the list and it is outside the current viewport,
  // keep the current zoom level but pan the chart so the trade becomes visible.
  useEffect(() => {
    if (!focusTradeId || !chartRef.current) return;
    const selectedTrade = visibleTrades.find((t) => t.trade_id === focusTradeId);
    if (!selectedTrade?.entry_time) {
      onFocusTradeHandled?.();
      return;
    }

    const rawEntryTs = toUtcTimestampSeconds(selectedTrade.entry_time);
    const rawExitTs = selectedTrade.exit_time
      ? toUtcTimestampSeconds(selectedTrade.exit_time)
      : null;
    const entryPlot = resolveEntryPlotOnCandles(preparedCandles, rawEntryTs, rawExitTs);
    if (!entryPlot) {
      onFocusTradeHandled?.();
      return;
    }
    const entryTs = entryPlot.time as UTCTimestamp;

    const timeScale = chartRef.current.timeScale();
    const currentRange = visibleRange ?? safeGetVisibleRange(timeScale);
    const currentFrom = currentRange?.from != null ? Number(currentRange.from) : null;
    const currentTo = currentRange?.to != null ? Number(currentRange.to) : null;

    if (currentFrom != null && currentTo != null && entryTs >= currentFrom && entryTs <= currentTo) {
      onFocusTradeHandled?.();
      return;
    }

    const currentSpan =
      currentFrom != null && currentTo != null
        ? Math.max(10, currentTo - currentFrom)
        : Math.max(10, secondsPerCandle * 120);
    let targetFrom = Number(entryTs) - currentSpan / 2;
    let targetTo = Number(entryTs) + currentSpan / 2;

    // Clamp pan range to known candle domain when possible.
    if (candleMinTime != null && candleMaxTime != null) {
      if (targetFrom < candleMinTime) {
        const shift = candleMinTime - targetFrom;
        targetFrom += shift;
        targetTo += shift;
      }
      if (targetTo > candleMaxTime) {
        const shift = targetTo - candleMaxTime;
        targetFrom -= shift;
        targetTo -= shift;
      }
      targetFrom = Math.max(targetFrom, candleMinTime);
      targetTo = Math.min(targetTo, candleMaxTime);
    }

    try {
      timeScale.setVisibleRange({
        from: Math.floor(targetFrom) as UTCTimestamp,
        to: Math.ceil(targetTo) as UTCTimestamp,
      });
    } catch (_) {
      // ignore if chart/time scale is not ready
    }
    onFocusTradeHandled?.();
  }, [
    focusTradeId,
    onFocusTradeHandled,
    visibleTrades,
    preparedCandles,
    visibleRange,
    secondsPerCandle,
    candleMinTime,
    candleMaxTime,
  ]);


  return (
    <Box position="relative" w="100%" h={`${height}px`}>
      {showNavigationControls ? (
        <HStack
          position="absolute"
          top={2}
          right={2}
          zIndex={3}
          spacing={0.5}
          borderWidth="1px"
          borderColor={navBtnBorder}
          borderRadius="md"
          bg={navBtnBg}
          p={0.5}
          boxShadow="sm"
        >
          <Tooltip label="Pan left" hasArrow openDelay={400}>
            <IconButton
              aria-label="Pan chart left"
              icon={<ChevronLeft size={16} />}
              size="xs"
              variant="ghost"
              isDisabled={navigationDisabled}
              onClick={() => panChart(-1)}
            />
          </Tooltip>
          <Tooltip label="Pan right" hasArrow openDelay={400}>
            <IconButton
              aria-label="Pan chart right"
              icon={<ChevronRight size={16} />}
              size="xs"
              variant="ghost"
              isDisabled={navigationDisabled}
              onClick={() => panChart(1)}
            />
          </Tooltip>
          <Tooltip label="Zoom in" hasArrow openDelay={400}>
            <IconButton
              aria-label="Zoom in"
              icon={<ZoomIn size={16} />}
              size="xs"
              variant="ghost"
              isDisabled={navigationDisabled}
              onClick={() => zoomChart(true)}
            />
          </Tooltip>
          <Tooltip label="Zoom out (show all loaded candles)" hasArrow openDelay={400}>
            <IconButton
              aria-label="Zoom out"
              icon={<ZoomOut size={16} />}
              size="xs"
              variant="ghost"
              isDisabled={navigationDisabled}
              onClick={() => zoomChart(false)}
            />
          </Tooltip>
        </HStack>
      ) : null}
      {instrumentLabel ? (
        <Badge
          variant="outline"
          data-trade-review-instrument-badge=""
          className={cn(
            REPORTS_SYMBOL_BADGE_CLASS,
            "pointer-events-none absolute left-2 top-2 z-[2] bg-background/90 backdrop-blur-[2px]"
          )}
        >
          {instrumentLabel}
        </Badge>
      ) : null}
      <Box
        ref={chartContainerRef}
        w="100%"
        h="100%"
        borderRadius="md"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      />
    </Box>
  );
};

export default ExecutionCandlesChart;

